import { ExternalLink } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import axios from 'axios';
import { QuizrrStepper } from './Stepper/QuizrrStepper';
import ProgressAnalysis from './ProgressAnalysis/ProgressAnalysis';
import AllTestsList from './AllTestsList/AllTestsList';
import Resources from './Resources/Resources';
import { Badge } from '../components/ui/badge';
import { TestSchedule } from './Calender/TestSchedule';
import DashboardChart from './DashboardChart/DashboardChart';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/selectDashboard';
import { useNavigate } from 'react-router-dom';
import TopHeader from '../Layout/TopHeader';
import { OnboardingModal } from '../Auth/AuthOnboarding/OnboardingModal';
import { HeartIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import Banner from '../components/generic/Banner';
import NoTestSeries from '../TestSeries/NoTestSeriesBought/NoTestSeries';
import ExploreSection from './ExploreSection/ExploreSection';
import ExploreTestSeries from './ExploreTestSeries/ExploreTestSeries';
import usePromotionPacks from './ExploreTestSeries/usePromotionPacks';
import { ThemeContext } from '../../../../apps/frontend/src/context/ThemeStore';
import DashboardLectures from './Lectures/Lectures';
import { PackSwitchSection } from './PackSwitchSection';
import DashboardBannerModal from './DashboardBannerModal';
import { openBannerLink } from './bannerLinkUtils';

function Dashboard() {
  const themeContext = useContext(ThemeContext);
  const isDarkTheme = Boolean(themeContext?.isDarkTheme);
  const { purchasedPacks, freePacks, selectedPack, purchasedFreePacksCollective, handlePackChange, refetchPacks } =
    usePackStorage();
  const { packs: promotionPacks } = usePromotionPacks();

  const [isExpanded, setIsExpanded] = useState(false);
  const [userProfile, setUserProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile'));
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isHelloBarVisible, setIsHelloBarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('right');
  const [timerVersion, setTimerVersion] = useState(0);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if HelloBar was previously closed
    const isClosed = sessionStorage.getItem('hellobarClosed');
    if (isClosed) {
      setIsHelloBarVisible(false);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleHelloBarClose = () => {
    setIsHelloBarVisible(false);
  };

  const savePackIdToStorage = (packId) => {
    try {
      localStorage.setItem('selectedPackId', packId);
    } catch (error) {
      console.error('Error saving pack ID to localStorage:', error);
    }
  };

  const dashboardScrollRef = useRef(null);
  const touchStartXRef = useRef(null);
  const didSwipeRef = useRef(false);

  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    const fetchPacks = async () => {
      const apiUrl = `/api/packs/v3`;
      try {
        setIsLoading(true);
        const response = await axios.get(apiUrl);
        const packs = response?.data?.data?.purchasedPacks;
        const notPurchasedPacks = response?.data?.data?.notPurchasedPacks;

        if (!packs || !packs?.length) {
          savePackIdToStorage(notPurchasedPacks[0]._id);
          setSelectedPack(notPurchasedPacks[0]);
        } else return;

        if (packs?.length > 0) {
          const savedPackId = localStorage.getItem('selectedPackId');
          if (savedPackId && packs?.length > 0) {
            const savedPack = packs.find((pack) => pack._id === savedPackId);
            if (savedPack) {
              setSelectedPack(savedPack);
            } else {
              savePackIdToStorage(packs[0]._id);
              setSelectedPack(packs[0]);
            }
          } else {
            savePackIdToStorage(packs[0]._id);
            setSelectedPack(packs[0]);
          }
        }
      } catch (error) {
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // fetchPacks();
  }, [navigate]);

  useEffect(() => {
    async function checkOnboardingStatus() {
      const res = await axios.get('/api/auth/user');
      const user = res?.data?.data;
      const hasCompletedOnboarding = user?.learningModeDetails && user?.isOnboardingV2Done;
      if (!hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
    }
    checkOnboardingStatus();
  }, []);

  useEffect(() => {
    setShowBannerModal(false);
    setCurrentBannerIndex(0);
    axios.get(`/api/banner/${selectedPack?._id}`).then((res) => {
      setBanners(res?.data?.data);
    });
  }, [selectedPack]);

  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    const interval = setInterval(() => {
      setSlideDirection('right');
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [banners, timerVersion]);

  useEffect(() => {
    if (document.getElementById('banner-carousel-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'banner-carousel-keyframes';
    style.textContent = `
      @keyframes bannerSlideInRight {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      @keyframes bannerSlideInLeft {
        from { transform: translateX(-100%); }
        to { transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const goToNext = () => {
    setSlideDirection('right');
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    setTimerVersion((v) => v + 1);
  };

  const goToPrev = () => {
    setSlideDirection('left');
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
    setTimerVersion((v) => v + 1);
  };

  const goToBannerIndex = (idx) => {
    setSlideDirection(idx > currentBannerIndex ? 'right' : 'left');
    setCurrentBannerIndex(idx);
    setTimerVersion((v) => v + 1);
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
    didSwipeRef.current = false;
  };

  const handleTouchEnd = (e) => {
    if (touchStartXRef.current === null || !banners || banners.length <= 1) return;
    const delta = touchStartXRef.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) >= 50) {
      didSwipeRef.current = true;
      if (delta > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
    touchStartXRef.current = null;
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (!packs || packs.length === 0) {
      navigate('/no-test-series-purchased');
    } else return;
  };

  const [isInitialBounce, setIsInitialBounce] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialBounce(false);
    }, 4500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    window.webengage.track('Viewed Dashboard', {
      'User Id': user?.email,
    });
  }, []);

  if (purchasedFreePacksCollective?.length === 0) {
    return <NoTestSeries />;
  }

  const dashboardBanner = banners?.[currentBannerIndex];
  const hasDarkBannerAssets = Boolean(dashboardBanner?.bannerDark && dashboardBanner?.mobileBannerDark);
  const useDarkPackBanner = isDarkTheme && hasDarkBannerAssets;
  const isPurchasedSelected = Boolean(selectedPack?._id) && purchasedPacks?.some((p) => p._id === selectedPack._id);

  const packBannerSrc = dashboardBanner
    ? isMobile
      ? useDarkPackBanner
        ? dashboardBanner.mobileBannerDark
        : dashboardBanner.mobileBanner
      : useDarkPackBanner
        ? dashboardBanner.bannerDark
        : dashboardBanner.banner
    : null;

  const bannerModalDetails = dashboardBanner?.modalDetails;
  const isBannerClickable = Boolean(dashboardBanner?.url || bannerModalDetails);

  const handleBannerClick = () => {
    if (didSwipeRef.current) return;
    if (dashboardBanner?.url) {
      openBannerLink(dashboardBanner.url, navigate);
      return;
    }
    if (bannerModalDetails) {
      setShowBannerModal(true);
    }
  };

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      {/* Full-width scrollable container */}
      <div
        ref={dashboardScrollRef}
        className={`
        h-screen overflow-y-auto 
        transition-all duration-700 ease-in-out 
        flex justify-center
      `}
        style={{
          width: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Inner fixed-width content container */}
        <div
          className={`
          flex flex-col py-4
          transition-all duration-700 ease-in-out
          w-full max-w-[1090px]
          pt-8
        `}
        >
          {/* <div
          className={`
            ${!isExpanded ? '' : '-mx-4 md:-mx-4 -my-4'}
            ${isHelloBarVisible ? 'mb-8' : ''}
            lg:mb-0
          `}
        >
          <HelloBar isCollapsed={!isExpanded} onClose={handleHelloBarClose} />
        </div> */}

          <div className="">
            <Banner />
          </div>

          <TopHeader />

          <div className="relative mb-8 mt-2 md:mt-0 flex-shrink-0 px-4 md:px-2 flex justify-between w-full items-center">
            {purchasedFreePacksCollective?.length > 0 && (
              <Select value={selectedPack?._id} onValueChange={handlePackChange}>
                <SelectTrigger
                  size="lg"
                  className="min-w-[120px] max-w-[90vw] w-fit !bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] cursor-pointer rounded-4xl shadow-none py-1 lg:py-2"
                >
                  <SelectValue placeholder={<div className="font-semibold">Select Test</div>} />
                </SelectTrigger>
                <SelectContent className="rounded-xl bg-[var(--q3-surface-default)] shadow-lg ">
                  <SelectGroup>
                    {purchasedFreePacksCollective?.map((each) => {
                      const isSelected = selectedPack?._id === each._id;
                      return (
                        <SelectItem
                          className={`px-4 py-4 font-semibold cursor-pointer w-full ${
                            isSelected
                              ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                              : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                          }`}
                          key={each._id}
                          value={each?._id}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <img alt="img" src={each?.icon || '/placeholder.svg'} className="h-5 w-5 md:h-6 md:w-6" />
                            <p className="font-medium whitespace-nowrap" style={{ fontSize: '16px' }}>
                              <span className="hidden sm:inline">{each?.title}</span>
                              <span className="inline sm:hidden">
                                {each?.shortTitle || each?.exam?.title || each?.title}
                              </span>
                            </p>
                          </div>
                        </SelectItem>
                      );
                    })}
                    <SelectItem
                      value="view-other-tests"
                      className="px-4 py-4 cursor-pointer text-[var(--q3-neutral-default)] w-full"
                    >
                      <div className="flex items-center justify-between w-full gap-3">
                        <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
                          View Other Test Series
                        </span>
                        <ExternalLink className="w-4 h-4 text-[var(--q3-neutral-default)] flex-shrink-0" />
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
            {purchasedPacks?.length > 0 && (
              <div
                style={{ right: -13.5 }}
                className="cursor-pointer flex lg:hidden"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Badge variant="outline" className="border-2 items-center gap-1 rounded-3xl cursor-pointer py-1 px-3">
                  <Calendar style={{ width: 'auto', height: '20px' }} />
                </Badge>
              </div>
            )}
          </div>

          {/* show banner based on device; dark assets only when theme is dark and both URLs exist */}
          {dashboardBanner && packBannerSrc && (
            <div className="mb-5">
              <div className="relative overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <img
                  key={currentBannerIndex}
                  src={packBannerSrc}
                  className={`p-3 md:p-0 w-full h-auto ${isBannerClickable && 'cursor-pointer'}`}
                  alt=""
                  onClick={handleBannerClick}
                  style={{
                    animation: `${slideDirection === 'right' ? 'bannerSlideInRight' : 'bannerSlideInLeft'} 0.35s ease-out`,
                  }}
                />
                {banners?.length > 1 && (
                  <>
                    <button
                      onClick={goToPrev}
                      className="absolute left-4 md:left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors duration-200"
                    >
                      <CaretLeftIcon size={18} weight="bold" />
                    </button>
                    <button
                      onClick={goToNext}
                      className="absolute right-4 md:right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors duration-200"
                    >
                      <CaretRightIcon size={18} weight="bold" />
                    </button>
                  </>
                )}
              </div>
              {banners?.length > 1 && (
                <div className="flex justify-center items-center gap-1.5 mt-2">
                  {banners.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToBannerIndex(idx)}
                      className={`rounded-full transition-all duration-300 ${
                        idx === currentBannerIndex
                          ? 'w-4 h-1.5 bg-[var(--q3-neutral-default)] opacity-40'
                          : 'w-1.5 h-1.5 bg-[var(--q3-neutral-default)] opacity-30'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <DashboardBannerModal
            open={showBannerModal}
            onOpenChange={setShowBannerModal}
            modalDetails={bannerModalDetails}
          />

          {purchasedPacks?.length > 0 && isPurchasedSelected && (
            <div className="px-4 md:px-2">
              <PackSwitchSection
                originalPackId={selectedPack._id}
                currentPack={selectedPack}
                enabled={isPurchasedSelected}
                refetchPacks={refetchPacks}
              />
            </div>
          )}

          <div className="mb-30">
            {purchasedPacks?.length > 0 && (
              <div>
                <QuizrrStepper packId={selectedPack?._id} />
              </div>
            )}
            {purchasedPacks?.length > 0 && (
              <div>
                <ProgressAnalysis packId={selectedPack?._id} />
              </div>
            )}
            <DashboardLectures packId={selectedPack?._id} />

            <div className="mt-5">
              <DashboardChart packId={selectedPack?._id} />
            </div>
            <div className={`${freePacks?.length > 0 ? 'mt-0' : 'mt-5'}`}>
              <AllTestsList packId={selectedPack?._id} />
            </div>
            <div className="mt-10">
              {selectedPack?.resources && selectedPack?.resources?.length > 0 && (
                <Resources packId={selectedPack?._id} packData={selectedPack} />
              )}
            </div>
            {promotionPacks && promotionPacks?.length > 0 && (
              <div className="mt-10">
                <ExploreTestSeries />
              </div>
            )}
            <div className="mt-10">
              <ExploreSection />
            </div>

            <div className="text-[28px] md:text-[40px] text-[var(--q3-neutral-light-v3)] font-extrabold p-2 mt-10 mb-24 md:mb-0 leading-snug text-start">
              <p className="text-wrap">
                Made with&nbsp;
                <HeartIcon
                  weight="fill"
                  className={`inline-block w-8 h-8 align-middle text-[var(--q3-danger-normal)] throb-on-hover ${
                    isInitialBounce ? 'throb-animation' : ''
                  }`}
                />
                &nbsp;by the Quizrr team, just for aspirants like you.
              </p>
              <p className="mt-4 text-3xl font-bold opacity-35" style={{ fontFamily: "'Caveat', cursive" }}>
                #PaperPhodnaHai
              </p>
            </div>
          </div>
        </div>
      </div>

      {purchasedPacks?.length > 0 && (
        <div
          className={`
          flex-shrink-0 h-screen overflow-hidden
          transition-all duration-700 ease-in-out
          ${isExpanded ? 'w-[320px] lg:w-[320px]' : 'sm:w-0'}
        `}
        >
          <div className="h-full">
            <TestSchedule isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      <OnboardingModal open={showOnboarding} onComplete={handleOnboardingComplete} />
    </AppLayout>
  );
}

export default Dashboard;
